import { NIC_RESOURCE } from '../../components/nicPricing/utils';
import { buildDiskPricingWatchFields, buildVendorDeviceMetaWatchFields } from '../../components/vendorDevice';
import type { ResourceConfigEntry, ResourceType, SkuModuleMeta } from '../../platform/pipeline-kernel/types';

export const vcpu: ResourceType = {
  requirement: '',
  code: 'vCPU',
  description: 'CPU',
  unit: 'Core',
};

export const memory: ResourceType = {
  requirement: '',
  code: 'Memory',
  description: 'Memory',
  unit: 'Gi',
};

export const gpu: ResourceType = {
  requirement: '',
  code: 'vGPU',
  description: 'GPU',
  unit: 'Card',
};

export const systemDiskSize: ResourceType = {
  requirement: '',
  code: 'SystemVolume',
  description: 'System Volume',
  unit: 'Gi',
};

export const dataVolume: ResourceType = {
  requirement: '',
  code: 'DataVolume',
  description: 'Data Volume',
  unit: 'Gi',
};

export const computeTemplates = { vcpu, memory, gpu };

/**
 * 节点组 JSON / 各拓扑副本变更须进入 resourceConfigs.watchFields，
 * 才能被 hasResourceWatchFieldChange 判定为 isResourceChange 并触发算价。
 */
const MI_NODE_REPLICA_WATCH_FIELDS = [
  'masterNodes',
  'workerNodes',
  'gatewayNodes',
  'runMode',
  'replicas',
  'prefillMasterNodes',
  'prefillWorkerNodes',
  'decodeMasterNodes',
  'decodeWorkerNodes',
  'prefillRunMode',
  'decodeRunMode',
  'prefillReplicas',
  'decodeReplicas',
  'kvOffloadRunMode',
  'kvOffloadMasterNodes',
  'kvOffloadWorkerNodes',
  'kvOffloadReplicas',
] as const;

/** useResourceChange：各资源监听字段与展示行映射 */
export const MI_RESOURCE_CONFIGS: ResourceConfigEntry[] = [
  {
    ...vcpu,
    watchFields: [...MI_NODE_REPLICA_WATCH_FIELDS],
    displayIds: ['compute-cpu-total'],
  },
  {
    ...memory,
    watchFields: [...MI_NODE_REPLICA_WATCH_FIELDS],
    displayIds: ['compute-memory-total'],
  },
  {
    ...gpu,
    watchFields: [...MI_NODE_REPLICA_WATCH_FIELDS],
    displayIds: ['compute-gpu-total'],
  },
  {
    ...systemDiskSize,
    watchFields: [
      'master.systemDiskSize',
      'worker.systemDiskSize',
      ...buildVendorDeviceMetaWatchFields('masterSystemDiskPricing'),
      ...buildVendorDeviceMetaWatchFields('workerSystemDiskPricing'),
      ...buildVendorDeviceMetaWatchFields('prefillMasterSystemDiskPricing'),
      ...buildVendorDeviceMetaWatchFields('prefillWorkerSystemDiskPricing'),
      ...buildVendorDeviceMetaWatchFields('decodeMasterSystemDiskPricing'),
      ...buildVendorDeviceMetaWatchFields('decodeWorkerSystemDiskPricing'),
      'prefill.master.systemDiskSize',
      'prefill.worker.systemDiskSize',
      'decode.master.systemDiskSize',
      'decode.worker.systemDiskSize',
      'kvOffload.master.systemDiskSize',
      'kvOffload.worker.systemDiskSize',
      ...buildVendorDeviceMetaWatchFields('kvOffloadMasterSystemDiskPricing'),
      ...buildVendorDeviceMetaWatchFields('kvOffloadWorkerSystemDiskPricing'),
      'kvOffloadRunMode',
      'kvOffloadMasterNodes',
      'kvOffloadWorkerNodes',
      'runMode',
      'masterNodes',
      'workerNodes',
      'gatewayNodes',
      'gateway.systemDiskSize',
      ...buildVendorDeviceMetaWatchFields('gatewaySystemDiskPricing'),
    ],
    displayIds: ['system-volume'],
  },
  {
    ...dataVolume,
    watchFields: ['_appStorageGroups', '_primaryDataVolume', 'model.sources', ...buildDiskPricingWatchFields('_primaryDataVolume')],
    displayIds: ['shared-storage-total'],
  },
  {
    ...NIC_RESOURCE,
    watchFields: [...MI_NODE_REPLICA_WATCH_FIELDS, 'network.nicPricing'],
    displayIds: ['model_network_segments'],
  },
  {
    requirement: '',
    code: 'SkuModule',
    description: 'SKU Module Selection',
    pricing: false,
    watchFields: ['skuResourceModules'],
  },
  {
    requirement: '',
    code: 'DisplayMeta',
    description: 'Display Meta',
    pricing: false,
    watchFields: ['model.sources', 'framework', '_inferenceSystem', '_modelCategory', 'runMode'],
    displayIds: ['model_name', 'inference_framework', 'deploy_mode'],
  },
];

/** SKU 模块：资源 / 非资源、下单预置判定 */
export const MI_SKU_MODULE_META: Record<string, SkuModuleMeta> = {
  baseInfo: {
    resource: false,
    presetCheckFields: ['alias'],
  },
  runtimeConfig: {
    resource: false,
    presetCheckFields: ['runtime.type', '_inferencePdSeparation'],
    fieldPresetPaths: {
      'mi.runtime.type': ['runtime.type'],
      'mi.runtime.workload': ['runtime.workload'],
      'mi.deployMode': ['_inferencePdSeparation'],
    },
  },
  modelStorage: {
    resource: false,
    presetCheckFields: ['model', 'model.sources', '_primaryDataVolume', '_modelCategory'],
  },
  envVars: {
    resource: false,
    presetCheckFields: ['commonEnv'],
    orderListItemLock: true,
  },
  nodeGroups: {
    resource: true,
    presetCheckFields: [
      '_inferencePdSeparation',
      'masterNodes',
      'workerNodes',
      'runMode',
      'commonEnv',
      'prefillMasterNodes',
      'prefillWorkerNodes',
      'decodeMasterNodes',
      'decodeWorkerNodes',
      'prefillReplicas',
      'decodeReplicas',
    ],
  },
  gatewayConfig: {
    resource: true,
    presetCheckFields: ['gatewayNodes'],
  },
  kvOffload: {
    resource: true,
    presetCheckFields: ['kvOffloadMasterNodes', 'kvOffloadWorkerNodes', 'kvOffloadRunMode'],
  },
  service: {
    resource: false,
    presetCheckFields: ['_inferenceService', '_serviceMountGroupName'],
    orderListItemLock: true,
  },
  storage: {
    resource: true,
    presetCheckFields: ['_imageStorageVolumes', '_appStorageGroups', '_primaryDataVolume', 'model.sources'],
  },
  initCommand: { resource: false },
  taskConfig: { resource: false },
  /** @deprecated 历史合并 key；新代码请用 modelStorage / systemConfig / service */
  modelInference: {
    resource: false,
    presetCheckFields: [
      'model',
      'model.name',
      'framework',
      'maasInferenceSource',
      '_inferenceSystem',
      '_inferenceService',
      'commonEnv',
      'inferenceFrameworkImage',
    ],
  },
};
